import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {radius, weight} from '../scale.js';

export type WedgeBv2OutreachLook = 'bv2oc-primary' | 'bv2oc-plain' | 'bv2oc-regen' | 'bv2oc-who' | 'bv2oc-who-on' | 'bv2oc-channel';
export type WedgeBv2OutreachAreaVariant = 'wedge-bv2-outreach-area';

const BV2_OUTREACH_LOOKS = new Set<string>(['bv2oc-primary', 'bv2oc-plain', 'bv2oc-regen', 'bv2oc-who', 'bv2oc-who-on', 'bv2oc-channel']);
export const isWedgeBv2OutreachAreaVariant = (variant: unknown): variant is WedgeBv2OutreachAreaVariant => variant === 'wedge-bv2-outreach-area';
export const isWedgeBv2OutreachLook = (look: string): look is WedgeBv2OutreachLook => BV2_OUTREACH_LOOKS.has(look);

/* Released brief v2 outreach channel action (prep-brief-v2 Bv2OcAction), a link or a button: 46px high with 18px side padding, a 14px
   radius, 14.5px bold type and no underline. The primary look has no border and takes a fill, an ink and a glow; the plain look has a 1px
   border and the card fill with the text ink. Disabled (a button only) takes the field fill, the muted ink, no glow and the not-allowed cursor.
   The caller sets the font family and points the --wedge-bv2-* roles at the brief scope vars; the app also hands in the primary fill,
   ink and glow (--wedge-bv2-oc-fill, --wedge-bv2-oc-ink and --wedge-bv2-oc-glow), which follow the channel tone. Hover, press, focus
   and pulse stay with the brief's own class rules. The box is 46px high in the shipped screen and had no transparent target there. No look
   draws one (wedgeBv2OutreachDrawsHit): the actions sit in a wrapping row and an anchor would add a positioned box that legacy does not have. */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
export const wedgeBv2OutreachDrawsHit = (look: string): boolean => !isWedgeBv2OutreachLook(look);

/* The other four looks are the pickers and the redraft control of the same tab. Regenerate (bv2oc-regen) is a 34px text button with a 10px radius,
   no border or fill, the accent ink and 13.5px bold type; it keeps the default cursor while disabled. A who tile (bv2oc-who, and -on for the
   picked one) is a 14px radius row that takes a 1px line border and the card fill, or a 1.5px accent border and the soft accent fill when picked.
   A channel tab (bv2oc-channel) is a 16px radius column that never shrinks below its content; its border, fill and glow follow the channel tone,
   so the caller passes them, and a disabled one is half clear with the not-allowed cursor. The caller sets the font family, the padding of a
   channel tab (it follows the phone width) and the type size and color of its inner marks. */
export function wedgeBv2OutreachStyle(look: WedgeBv2OutreachLook, disabled = false): CSSProperties {
  if (look === 'bv2oc-regen') {
    return {
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: radius.md, border: 'none', background: 'transparent',
      color: role('acc-ink', T.accent), fontSize: 13.5, fontWeight: weight.bold, cursor: disabled ? 'default' : 'pointer',
    };
  }
  if (look === 'bv2oc-who' || look === 'bv2oc-who-on') {
    const on = look === 'bv2oc-who-on';
    return {
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 8px 8px', borderRadius: radius.xl, flexShrink: 0, cursor: 'pointer', textAlign: 'left',
      border: on ? `1.5px solid ${role('acc', T.accent)}` : `1px solid ${role('line', T.separator)}`, background: on ? role('acc-soft', T.surfaceAlt) : role('card2', T.surfaceAlt),
    };
  }
  if (look === 'bv2oc-channel') {
    return {
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, borderRadius: radius.card, textAlign: 'left', minWidth: 0,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    };
  }
  const primary = look === 'bv2oc-primary';
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 46, padding: '0 18px', borderRadius: radius.xl,
    border: primary ? 'none' : `1px solid ${role('line2', T.separator)}`, cursor: disabled ? 'not-allowed' : 'pointer', textDecoration: 'none',
    background: disabled ? role('field', T.surfaceAlt) : primary ? role('oc-fill', T.accent) : role('card', T.surface),
    color: disabled ? role('muted', T.labelSec) : primary ? role('oc-ink', T.label) : role('text', T.label),
    boxShadow: primary && !disabled ? role('oc-glow', 'none') : 'none',
    fontSize: 14.5, fontWeight: weight.bold,
  };
}

/* The draft box of a short channel (prep-brief-v2 Bv2OcDraft): one bare native textarea at full width, border-box, resizable by height, 104px at
   least, 14px radius, 14px 16px padding, 1px line border, the field fill, the text ink, 15px type at 1.5 leading and no outline. The caller passes the
   font family and points the --wedge-bv2-* roles at the brief scope vars. */
export function wedgeBv2OutreachAreaStyle(_variant: WedgeBv2OutreachAreaVariant): CSSProperties {
  return {
    width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 104, padding: '14px 16px', borderRadius: radius.xl,
    border: `1px solid ${role('line2', T.separator)}`, background: role('field', T.surfaceAlt), color: role('text', T.label),
    fontSize: 15, lineHeight: 1.5, outline: 'none',
  };
}
